import React from 'react';
import { Plus, X, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * RepeatingBlock - Dynamic add/remove entry blocks.
 * Props:
 *   entries: array of entry objects
 *   onChange: (newEntries) => void
 *   renderEntry: (entry, index, updateEntry) => JSX
 *   createEmpty: () => object (factory for new empty entry)
 *   maxEntries: number (default 20)
 *   addLabel: string
 *   prompt: string (instructional text above)
 *   entryTitle: (entry, index) => string
 */
export function RepeatingBlock({
  entries = [], onChange, renderEntry, createEmpty,
  maxEntries = 20, addLabel = 'Add Entry', prompt, entryTitle
}) {
  const [collapsed, setCollapsed] = React.useState({});

  const addEntry = () => {
    if (entries.length < maxEntries) {
      onChange([...entries, createEmpty()]);
    }
  };

  const removeEntry = (index) => {
    const updated = entries.filter((_, i) => i !== index);
    onChange(updated);
    const newCollapsed = {};
    Object.keys(collapsed).forEach(k => {
      const ki = parseInt(k);
      if (ki < index) newCollapsed[ki] = collapsed[ki];
      else if (ki > index) newCollapsed[ki - 1] = collapsed[ki];
    });
    setCollapsed(newCollapsed);
  };

  const updateEntry = (index, updatedEntry) => {
    const updated = [...entries];
    updated[index] = updatedEntry;
    onChange(updated);
  };

  const toggleCollapse = (index) => {
    setCollapsed(prev => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <div>
      {prompt && (
        <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: 12, lineHeight: 1.5, fontStyle: 'italic' }}>
          {prompt}
        </p>
      )}
      {entries.map((entry, index) => (
        <div
          key={index}
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            marginBottom: 10,
            backgroundColor: '#fafbfc',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 14px', backgroundColor: '#f3f4f6', cursor: 'pointer',
              borderBottom: collapsed[index] ? 'none' : '1px solid #e5e7eb',
            }}
            onClick={() => toggleCollapse(index)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#4A90B5', minWidth: 24 }}>
                #{index + 1}
              </span>
              <span style={{ fontSize: '0.8125rem', color: '#374151', fontWeight: 500 }}>
                {entryTitle ? entryTitle(entry, index) : `Entry ${index + 1}`}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeEntry(index); }}
                style={{
                  padding: '2px 6px', borderRadius: 6, border: '1px solid #fca5a5',
                  backgroundColor: '#fef2f2', color: '#dc2626', cursor: 'pointer',
                  fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 2,
                }}
                data-testid={`remove-entry-${index}`}
              >
                <X size={12} /> Remove
              </button>
              {collapsed[index] ? <ChevronDown size={16} color="#9ca3af" /> : <ChevronUp size={16} color="#9ca3af" />}
            </div>
          </div>
          {!collapsed[index] && (
            <div style={{ padding: '12px 14px' }}>
              {renderEntry(entry, index, (updated) => updateEntry(index, updated))}
            </div>
          )}
        </div>
      ))}
      {entries.length < maxEntries && (
        <button
          type="button"
          onClick={addEntry}
          data-testid="add-entry-btn"
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', borderRadius: 8,
            border: '1.5px dashed #4A90B5', backgroundColor: '#f0f7fb',
            color: '#1e6b8a', fontSize: '0.8125rem', fontWeight: 600,
            cursor: 'pointer', transition: 'all 0.15s ease',
            width: '100%', justifyContent: 'center',
          }}
        >
          <Plus size={16} /> {addLabel} ({entries.length}/{maxEntries})
        </button>
      )}
    </div>
  );
}
